import React, {useState} from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Checkbox,
  Toggle,
  Text,
  Label,
  ListGroupItem
} from '@albert-io/atomic';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {ClassroomAssignmentSettings} from './RecipientSettings.types';
import {StudentModelV2} from 'resources/augmented/Student/StudentModel.v2';
import {Map, List} from 'immutable';
import {pluralize} from 'lib/stringUtils';

import './classroom-table.scss';

interface StudentsDropdownProps {
  classroomObj: ClassroomModelV1;
  selectedClassrooms: Map<string, ClassroomAssignmentSettings>;
  setSelectedClassrooms: Function;
}

const StudentsDropdown = ({
  classroomObj,
  selectedClassrooms,
  setSelectedClassrooms
}: StudentsDropdownProps) => {
  return (
    <Dropdown
      withMaxHeight
      trigger={
        <button type='button' className='classroom-table__dropdown-button'>
          {classroomObj.selectedStudents.size !== classroomObj.classroomInfo.getStudents().size ? (
            <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
              <Text>{`${classroomObj.selectedStudents.size} ${pluralize(
                'Student',
                classroomObj.selectedStudents.size
              )}`}</Text>
              <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
            </div>
          ) : (
            <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
              {classroomObj.classroomInfo.getStudents().isEmpty() ? (
                <Text size='m' className='u-mar-r_4' italic emphasis='lowest'>
                  No Students
                </Text>
              ) : (
                <Text size='m' className='u-mar-r_4'>
                  All Students
                </Text>
              )}
              <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
            </div>
          )}
        </button>
      }
      className='classroom-table__dropdown'
    >
      <StudentPopover
        classroomObj={classroomObj}
        selectedClassrooms={selectedClassrooms}
        setSelectedClassrooms={setSelectedClassrooms}
      />
    </Dropdown>
  );
};

interface StudentPopoverProps {
  classroomObj: ClassroomModelV1;
  selectedClassrooms: Map<string, ClassroomAssignmentSettings>;
  setSelectedClassrooms: Function;
}

const StudentPopover = ({
  classroomObj,
  selectedClassrooms,
  setSelectedClassrooms
}: StudentPopoverProps) => {
  const [studentList, setStudentList] = useState<List<StudentModelV2>>(
    classroomObj.selectedStudents
  );
  const [isToggleChecked, setIsToggleChecked] = useState(classroomObj.autoAssignNewEnrollees);
  const classroomId = classroomObj.classroomInfo.getId();

  const onApply = () => {
    let updatedClassrooms = selectedClassrooms;
    const newClassroomObj = {
      ...classroomObj,
      ...{
        selectedStudents: studentList,
        autoAssignNewEnrollees: isToggleChecked
      }
    };
    updatedClassrooms = selectedClassrooms.set(classroomId, newClassroomObj);
    setSelectedClassrooms(updatedClassrooms);
  };

  const handleStudentChange = (student) => {
    let updatedStudentList = studentList;
    if (updatedStudentList.includes(student)) {
      updatedStudentList = updatedStudentList
        .filter((stud) => stud.getId() !== student.getId())
        .toList();
    } else {
      updatedStudentList = updatedStudentList.push(student);
    }
    setStudentList(updatedStudentList);
  };

  return (
    <>
      {classroomObj.classroomInfo.getStudents().isEmpty() ? (
        <>
          <ListGroupItem as='div' onClick={(e) => e.stopPropagation()}>
            <Text
              size='m'
              emphasis='lowest'
              className='u-width_100pc u-text-align_center u-pad_2 u-border-radius_1 u-bgc_slate-100 u-display_block'
            >
              This class has no students.
            </Text>
          </ListGroupItem>
          <ListGroupItem
            as='div'
            onClick={(e) => e.stopPropagation()}
            className='classroom-table__student-dropdown-item'
          >
            <Label>
              <div className='classroom-table__future-enrollees-toggle'>
                Assign to future enrollees
                <Toggle
                  className='u-mar-l_1'
                  onChange={() => setIsToggleChecked(!isToggleChecked)}
                  checked={isToggleChecked}
                />
              </div>
            </Label>
          </ListGroupItem>
        </>
      ) : (
        <>
          <ListGroupItem
            as='div'
            onClick={(e) => e.stopPropagation()}
            className='classroom-table__student-dropdown-item'
          >
            {classroomObj.classroomInfo.getStudents().map((student) => {
              const isChecked = studentList.includes(student);
              return (
                <div className='classroom-table__student-name'>
                  <Label>
                    <Checkbox
                      aria-hidden={false}
                      className='u-mar-r_2'
                      size='s'
                      onChange={() => {
                        handleStudentChange(student);
                      }}
                      checked={isChecked}
                    />
                    {`${student.getFirstName()} ${student.getLastName()}`}
                  </Label>
                </div>
              );
            })}
            {studentList.isEmpty() && (
              <Text as='div' color='red' className='u-mar-b_1'>
                At least one student should be selected.
              </Text>
            )}
          </ListGroupItem>
          <ListGroupItem
            as='div'
            onClick={(e) => e.stopPropagation()}
            className='classroom-table__student-dropdown-item'
          >
            <Label>
              <div className='classroom-table__future-enrollees-toggle'>
                Assign to future enrollees
                <Toggle
                  className='u-mar-l_1'
                  onChange={() => setIsToggleChecked(!isToggleChecked)}
                  checked={isToggleChecked}
                />
              </div>
            </Label>
          </ListGroupItem>
        </>
      )}
      <div className='u-text-align_right u-mar-tb_1 u-mar-r_1'>
        <Button
          size='s'
          variant='text'
          onClick={() => {
            onApply();
          }}
        >
          Done
        </Button>
      </div>
    </>
  );
};

export default StudentsDropdown;
